// src/hooks/useClientAlerts.js
// Hook for fetching and calculating client data freshness alerts
// Works for both User Portal (user's clients) and Firm Portal (all org clients)

import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
    getEffectiveThresholds,
    calculateAllClientAlerts,
    DEFAULT_ALERT_THRESHOLDS
} from '../utils/alertUtils';

/**
 * Hook to fetch and calculate client alerts
 * 
 * @param {object} options
 * @param {string} options.organizationId - Organization ID (for firm portal)
 * @param {string} options.userId - User ID (for user portal - filters to user's clients)
 * @param {object} options.userSettings - User's alert settings (optional override)
 * @param {boolean} options.firmPortal - Whether this is for firm portal (all org clients)
 * @returns {object} { alerts, loading, error, totalAlertCount, dismissAlert, refetch }
 */
export function useClientAlerts({ 
    organizationId, 
    userId, 
    userSettings = null,
    firmPortal = false 
}) {
    const [clients, setClients] = useState([]);
    const [members, setMembers] = useState([]);
    const [firmSettings, setFirmSettings] = useState({});
    const [dismissedAlerts, setDismissedAlerts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch data
    const fetchData = useCallback(async () => {
        if (!organizationId && !userId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Fetch organization settings
            if (organizationId) {
                const orgRef = doc(db, 'organizations', organizationId);
                const orgSnap = await getDoc(orgRef);
                if (orgSnap.exists()) {
                    const orgData = orgSnap.data();
                    setFirmSettings(orgData.alertSettings || {});
                }
            }

            // Fetch clients
            let clientsQuery;
            if (firmPortal && organizationId) {
                // Firm portal: all org clients
                clientsQuery = query(
                    collection(db, 'clients'),
                    where('organizationId', '==', organizationId)
                );
            } else if (userId) {
                // User portal: user's clients only
                clientsQuery = query(
                    collection(db, 'clients'),
                    where('fcfoId', '==', userId)
                );
            } else {
                setLoading(false);
                return;
            }

            const clientsSnap = await getDocs(clientsQuery);
            const clientsList = clientsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // For firm portal, also fetch members to get FCFO names
            if (firmPortal && organizationId) {
                const membersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', organizationId)
                );
                const membersSnap = await getDocs(membersQuery);
                const membersList = membersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setMembers(membersList);

                // Attach FCFO names to clients
                const clientsWithFCFO = clientsList.map(client => {
                    const fcfo = membersList.find(m => m.id === client.fcfoId);
                    return {
                        ...client,
                        fcfoName: fcfo?.name || fcfo?.email || 'Unknown'
                    };
                });
                setClients(clientsWithFCFO);
            } else {
                setClients(clientsList);
            }

            // Fetch user's dismissed alerts
            if (userId) {
                const userRef = doc(db, 'users', userId);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    setDismissedAlerts(userSnap.data().dismissedAlerts || {});
                }
            }

            setLoading(false);
        } catch (err) {
            console.error('[useClientAlerts] Error fetching data:', err);
            setError(err.message);
            setLoading(false);
        }
    }, [organizationId, userId, firmPortal]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Calculate effective thresholds
    const thresholds = useMemo(() => {
        return getEffectiveThresholds(userSettings, firmSettings);
    }, [userSettings, firmSettings]);

    // Calculate alerts
    const alerts = useMemo(() => {
        if (loading || clients.length === 0) return [];
        return calculateAllClientAlerts(clients, thresholds, dismissedAlerts);
    }, [clients, thresholds, dismissedAlerts, loading]);

    // Total alert count (for badge)
    const totalAlertCount = useMemo(() => {
        return alerts.reduce((sum, client) => sum + client.alerts.length, 0);
    }, [alerts]);

    // Clients with alerts count
    const clientsWithAlertsCount = alerts.length;

    // Map alert type to dismissal key
    const getAlertDismissalKey = (alertType) => {
        switch (alertType) {
            case 'strategic_context':
                return 'strategic';
            case 'financials':
                return 'financials';
            case 'asana':
                return 'asana';
            default:
                return alertType;
        }
    };

    // Dismiss an alert for a client
    const dismissAlert = useCallback(async (clientId, alertType, dismissUserId = userId) => {
        if (!dismissUserId) {
            console.error('[useClientAlerts] Cannot dismiss: no userId');
            return false;
        }

        try {
            const userRef = doc(db, 'users', dismissUserId);
            const userSnap = await getDoc(userRef);
            const currentDismissed = userSnap.exists() 
                ? (userSnap.data().dismissedAlerts || {})
                : {};

            const updatedDismissed = {
                ...currentDismissed,
                [clientId]: {
                    strategic: serverTimestamp(),
                    financials: serverTimestamp(),
                    asana: serverTimestamp()
                }
            };

            await updateDoc(userRef, {
                dismissedAlerts: updatedDismissed
            });

            // Update local state
            setDismissedAlerts(updatedDismissed);
            return true;
        } catch (err) {
            console.error('[useClientAlerts] Error dismissing alert:', err);
            return false;
        }
    }, [userId]);

    // Dismiss all alerts for a client
    const dismissAllClientAlerts = useCallback(async (clientId, dismissUserId = userId) => {
        if (!dismissUserId) {
            console.error('[useClientAlerts] Cannot dismiss: no userId');
            return false;
        }

        try {
            const userRef = doc(db, 'users', dismissUserId);
            const userSnap = await getDoc(userRef);
            const currentDismissed = userSnap.exists() 
                ? (userSnap.data().dismissedAlerts || {})
                : {};

            const updatedDismissed = {
                ...currentDismissed,
                [clientId]: {
                    strategic: serverTimestamp(),
                    financials: serverTimestamp()
                }
            };

            await updateDoc(userRef, {
                dismissedAlerts: updatedDismissed
            });

            setDismissedAlerts(updatedDismissed);
            return true;
        } catch (err) {
            console.error('[useClientAlerts] Error dismissing alerts:', err);
            return false;
        }
    }, [userId]);

    return {
        alerts,
        loading,
        error,
        totalAlertCount,
        clientsWithAlertsCount,
        thresholds,
        firmSettings,
        dismissAlert,
        dismissAllClientAlerts,
        refetch: fetchData
    };
}

/**
 * Hook to manage alert settings (for settings UI)
 * 
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID (optional, for user-level settings)
 * @returns {object} { settings, loading, saveSettings }
 */
export function useAlertSettings(organizationId, userId = null) {
    const [firmSettings, setFirmSettings] = useState(null);
    const [userSettings, setUserSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            try {
                // Fetch firm settings
                if (organizationId) {
                    const orgRef = doc(db, 'organizations', organizationId);
                    const orgSnap = await getDoc(orgRef);
                    if (orgSnap.exists()) {
                        setFirmSettings(orgSnap.data().alertSettings || {});
                    }
                }

                // Fetch user settings
                if (userId) {
                    const userRef = doc(db, 'users', userId);
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        setUserSettings(userSnap.data().alertSettings || {});
                    }
                }
            } catch (err) {
                console.error('[useAlertSettings] Error fetching settings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [organizationId, userId]);

    // Save firm-level settings
    const saveFirmSettings = useCallback(async (newSettings) => {
        if (!organizationId) return false;
        
        setSaving(true);
        try {
            const orgRef = doc(db, 'organizations', organizationId);
            await updateDoc(orgRef, {
                alertSettings: newSettings,
                alertSettingsUpdatedAt: serverTimestamp()
            });
            setFirmSettings(newSettings);
            return true;
        } catch (err) {
            console.error('[useAlertSettings] Error saving firm settings:', err);
            return false;
        } finally {
            setSaving(false);
        }
    }, [organizationId]);

    // Save user-level settings
    const saveUserSettings = useCallback(async (newSettings) => {
        if (!userId) return false;
        
        setSaving(true);
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                alertSettings: newSettings
            });
            setUserSettings(newSettings);
            return true;
        } catch (err) {
            console.error('[useAlertSettings] Error saving user settings:', err);
            return false;
        } finally {
            setSaving(false);
        }
    }, [userId]);

    return {
        firmSettings: firmSettings || {},
        userSettings: userSettings || {},
        loading,
        saving,
        saveFirmSettings,
        saveUserSettings,
        defaults: DEFAULT_ALERT_THRESHOLDS
    };
}

export default useClientAlerts;